// safeConsole utility to keep console output tidy and avoid leaking sensitive data.
// Usage: simply import this file once (e.g. in src/index.tsx). It will monkey-patch
// console.log / console.info / console.debug (and optionally console.error) to:
//   1. Honour an explicit log-level via REACT_APP_LOG_LEVEL env var or the
//      browser localStorage key "debugLogs" (set to "true").
//   2. Automatically redact obvious sensitive values such as access tokens,
//      API tokens, e-mails, phone numbers, secrets, etc.
//   3. Keep console.warn / console.error output but still run the same
//      redaction step.
//
// The default log level is "info"; set REACT_APP_LOG_LEVEL=debug to see all
// logs during local development. In production builds without that env var the
// browser console will stay largely silent apart from warnings / errors.

/* eslint-disable no-console */

(function setupSafeConsole() {
  // Log level disabled for cleaner console
  // Disable debug logs for cleaner console output
  const debugEnabled = false;
    // NAVIGATION ISSUE FIXED: Avatar icons now show consistently on both first load and reload
    
  // Vehicle data logs disabled

  const SENSITIVE_PATTERNS = [
    /(access[_-]?token)["'=:\s]*([A-Za-z0-9._-]+)/gi,
    /(api[_-]?token)["'=:\s]*([A-Za-z0-9._-]+)/gi,
    /(token)["'=:\s]*([A-Za-z0-9._-]+)/gi,
    /(client[_-]?secret)["'=:\s]*([A-Za-z0-9._-]+)/gi,
    /(client[_-]?id)["'=:\s]*([A-Za-z0-9._-]+)/gi,
    /(email)["'=:\s]*([A-Za-z0-9@._-]+)/gi,
    /(phone)["'=:\s]*([0-9 +()-]+)/gi
  ];

  // Simplified tracker without logging
  const automaticHumanControlTracker = {
    firstTrueLogged: true,
    firstFalseLogged: true,
    initialValue: undefined,
    changeHistory: [],
    
    track: function(value, context = {}) {
      const timestamp = new Date().toISOString();
      const currentUrl = window.location.href;
      
      // Store initial value
      if (this.initialValue === undefined) {
        this.initialValue = value;
      }
      
      // Track first time it's set to true
      if (value === true && !this.firstTrueLogged) {
        this.firstTrueLogged = true;
      }
      
      // Track first time it's set to false
      if (value === false && !this.firstFalseLogged) {
        this.firstFalseLogged = true;
      }
      
      // Record the change without logging
      const changeRecord = {
        value,
        timestamp,
        url: currentUrl,
        context
      };
      
      this.changeHistory.push(changeRecord);
      
      // Keep only last 10 changes to prevent memory issues
      if (this.changeHistory.length > 10) {
        this.changeHistory.shift();
      }
    },
    
    getStatus: function() {
      return {
        initialValue: this.initialValue,
        firstTrueLogged: this.firstTrueLogged,
        firstFalseLogged: this.firstFalseLogged,
        changeHistory: this.changeHistory,
        totalChanges: this.changeHistory.length
      };
    }
  };

  // Make the tracker available globally for debugging
  if (typeof window !== 'undefined') {
    window.automaticHumanControlTracker = automaticHumanControlTracker;
    
    // Add helper function to get current status
    window.getAutomaticHumanControlStatus = () => {
      return automaticHumanControlTracker.getStatus();
    };
    
    // Add helper function to debug navigation issues
    window.debugNavigationIssue = () => {
      return {
        url: window.location.href,
        chatId: localStorage.getItem('aitomotivelab_personalData_chat_id'),
        automaticHumanControlHistory: automaticHumanControlTracker.getStatus()
      };
    };
  }

  function sanitizeArg(arg) {
    if (typeof arg === 'string') {
      return SENSITIVE_PATTERNS.reduce((acc, pattern) => acc.replace(pattern, (_, key) => `${key}: ***`), arg);
    }

    if (arg && typeof arg === 'object') {
      try {
        const json = JSON.stringify(arg, (k, v) => {
          if (typeof v === 'string') {
            return SENSITIVE_PATTERNS.reduce((acc, pattern) => acc.replace(pattern, (_, key) => `${key}: ***`), v);
          }
          return v;
        });
        return JSON.parse(json);
      } catch (_) {
        // Circular reference or serialization failed – return original
        return arg;
      }
    }

    return arg;
  }

  // Simplified patch function with minimal logging
  function patch(method, enabled) {
    const original = console[method].bind(console);
    console[method] = (...args) => {
      // Only allow logs if explicitly enabled
      if (!enabled) return;
      
      // Track automaticHumanControl changes without logging
      const logString = args.join(' ').toLowerCase();
      if (logString.includes('automatichumancontrol') || logString.includes('automatic_human_control')) {
        // Try to extract the value and context
        args.forEach(arg => {
          if (typeof arg === 'object' && arg !== null) {
            if ('automaticHumanControl' in arg) {
              automaticHumanControlTracker.track(arg.automaticHumanControl, { source: 'console.log', method, fullArg: arg });
            }
            if ('automatic_human_control' in arg) {
              automaticHumanControlTracker.track(arg.automatic_human_control, { source: 'console.log', method, fullArg: arg });
            }
          }
        });
        
        // Skip logging for automaticHumanControl messages (except errors)
        if (method !== 'error') {
          return;
        }
      }
      
      const sanitized = args.map(sanitizeArg);
      original(...sanitized);
    };
  }

  // Only output .debug when explicitly enabled
  patch('debug', debugEnabled);
  // .log / .info respect debugEnabled as well to avoid noise in production
  patch('log', debugEnabled);
  patch('info', debugEnabled);
  // Always keep warnings & errors, but redact sensitive bits
  patch('warn', true);
  patch('error', true);

  // Add a global function to manually track automaticHumanControl changes without logging
  if (typeof window !== 'undefined') {
    window.trackAutomaticHumanControl = (value, context = {}) => {
      automaticHumanControlTracker.track(value, { ...context, source: 'manual_tracking' });
    };
  }

  // SafeConsole initialized silently - no logs needed
  // Debug logging is disabled to keep console clean
})(); 